import { apiClient } from '../../api/client';
import { ENDPOINTS } from '../../api/endpoints';
import { ProductDetailsModel, ProductModel, ProductVersionModel, ProductRoadmapItemModel, ProductDocumentModel, ProductGalleryModel, ProductHistoryModel, ProductInvoiceLineModel } from '../../types/models/product';

function unwrap(data: any): any {
  if (data && typeof data === 'object' && 'success' in data && 'data' in data) return data.data;
  return data;
}

function mapBackendToFrontend(backend: any): ProductModel {
  if (!backend) return {} as ProductModel;
  const metadata = backend.metadata || {};
  const tags = Array.isArray(backend.tags) ? backend.tags : [];
  return {
    id: String(backend.id),
    organizationId: backend.organizationId,
    name: backend.name || '',
    sku: backend.sku || undefined,
    description: backend.description || undefined,
    type: backend.type,
    status: backend.status,
    tags,
    metadata,
    createdAt: backend.createdAt,
    updatedAt: backend.updatedAt,
    categories: metadata.categories,
    brand: metadata.brand,
    supplier: metadata.supplier,
    supplierCode: metadata.supplierCode,
    unit: metadata.unit || 'unidade',
    barcode: metadata.barcode,
    code: metadata.code,
    subcategory: metadata.subcategory,
    price: metadata.price,
    promotionalPrice: metadata.promotionalPrice,
    cost: metadata.cost,
    stock: metadata.stock,
    minimumStock: metadata.minimumStock,
    maxStock: metadata.maximumStock,
    reservedStock: metadata.reservedStock,
    availableStock: metadata.availableStock,
    images: (Array.isArray(metadata.images) ? metadata.images : metadata.image ? [metadata.image] : [])
      .filter((image: unknown): image is string => typeof image === 'string' && !image.startsWith('blob:')),
    image: typeof metadata.image === 'string' && !metadata.image.startsWith('blob:')
      ? metadata.image
      : (Array.isArray(metadata.images) ? metadata.images.find((image: unknown) => typeof image === 'string' && !image.startsWith('blob:')) : undefined),
    longDescription: metadata.longDescription,
    ncm: metadata.ncm,
    weight: metadata.weight,
    dimensions: metadata.dimensions,
    leadTime: metadata.leadTime,
    duration: metadata.duration,
    billingUnit: metadata.billingUnit,
  };
}

function mapFrontendToBackend(data: any): any {
  const metadata: Record<string, any> = {};
  const metadataFields = ['categories', 'brand', 'supplier', 'supplierCode', 'unit',
    'barcode', 'code', 'subcategory', 'price', 'promotionalPrice', 'cost', 'stock',
    'minimumStock', 'maximumStock', 'reservedStock', 'availableStock', 'longDescription',
    'ncm', 'weight', 'dimensions', 'leadTime', 'duration', 'billingUnit', 'image', 'images'];
  metadataFields.forEach((field) => {
    if (data[field] !== undefined && data[field] !== '' && data[field] !== null) {
      metadata[field] = data[field];
    }
  });
  return {
    name: data.name,
    sku: data.sku || null,
    description: data.description || data.longDescription || null,
    type: data.type,
    status: data.status,
    tags: Array.isArray(data.tags) ? data.tags : [],
    metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
  };
}

export class ProductsService {
  static async list(params?: Record<string, any>): Promise<ProductModel[]> {
    const response = await apiClient.get(ENDPOINTS.PRODUCTS.LIST, { params });
    const unwrapped = unwrap(response.data);
    return Array.isArray(unwrapped) ? unwrapped.map(mapBackendToFrontend) : [];
  }

  static async getById(id: string | number): Promise<ProductModel> {
    const response = await apiClient.get(ENDPOINTS.PRODUCTS.DETAILS(id));
    return mapBackendToFrontend(unwrap(response.data));
  }

  static async getDetails(id: string | number): Promise<ProductDetailsModel> {
    const response = await apiClient.get(ENDPOINTS.PRODUCTS.DETAILS_FULL(id));
    const data = unwrap(response.data);
    if (!data) return {} as ProductDetailsModel;
    const product = mapBackendToFrontend(data);
    return {
      ...product,
      versions: (data.versions || []).map((v: any) => ({
        id: String(v.id), productId: v.productId || String(data.id), versionTag: v.versionTag,
        changelog: v.changelog, releasedAt: v.releasedAt, createdAt: v.createdAt, updatedAt: v.updatedAt,
      })) as ProductVersionModel[],
      roadmapItems: (data.roadmapItems || []).map((r: any) => ({
        id: String(r.id), productId: r.productId || String(data.id), title: r.title,
        status: r.status, priority: r.priority, plannedRelease: r.plannedRelease,
        meta: r.meta, createdAt: r.createdAt, updatedAt: r.updatedAt,
      })) as ProductRoadmapItemModel[],
      documents: (data.documents || []).map((d: any) => ({
        id: String(d.id), filename: d.filename, mime: d.mime, size: d.size,
        url: d.url, createdAt: d.createdAt,
      })) as ProductDocumentModel[],
      gallery: (data.gallery || []).map((g: any) => ({
        id: String(g.id), url: g.url, filename: g.filename, alt: g.alt,
        isPrimary: g.isPrimary, order: g.order, createdAt: g.createdAt,
      })) as ProductGalleryModel[],
      history: (data.history || []).map((h: any) => ({
        id: String(h.id), action: h.action, user: h.user, time: h.time,
        beforeData: h.beforeData, afterData: h.afterData,
      })) as ProductHistoryModel[],
      invoiceLines: (data.invoiceLines || []).map((il: any) => ({
        id: String(il.id), invoiceId: il.invoiceId, invoiceNumber: il.invoiceNumber,
        description: il.description, quantity: il.quantity, unitPrice: il.unitPrice,
        total: il.total,
      })) as ProductInvoiceLineModel[],
    };
  }

  static async create(data: any): Promise<ProductModel> {
    const response = await apiClient.post(ENDPOINTS.PRODUCTS.CREATE, mapFrontendToBackend(data));
    return mapBackendToFrontend(unwrap(response.data));
  }

  static async update(id: string | number, data: any): Promise<ProductModel> {
    const response = await apiClient.put(ENDPOINTS.PRODUCTS.UPDATE(id), mapFrontendToBackend(data));
    return mapBackendToFrontend(unwrap(response.data));
  }

  static async delete(id: string | number): Promise<void> {
    await apiClient.delete(ENDPOINTS.PRODUCTS.DELETE(id));
  }
}

export const ProductVersionsService = {
  async listByProduct(productId: string | number): Promise<ProductVersionModel[]> {
    const response = await apiClient.get(ENDPOINTS.PRODUCT_VERSIONS.BY_PRODUCT(productId));
    const unwrapped = unwrap(response.data);
    return Array.isArray(unwrapped) ? unwrapped.map((v: any) => ({
      id: String(v.id), productId: String(v.productId), versionTag: v.versionTag,
      changelog: v.changelog, releasedAt: v.releasedAt, createdAt: v.createdAt, updatedAt: v.updatedAt,
    })) : [];
  },

  async create(data: { productId: string; versionTag: string; changelog?: string; releasedAt?: string }): Promise<ProductVersionModel> {
    const response = await apiClient.post(ENDPOINTS.PRODUCT_VERSIONS.CREATE, data);
    const v = unwrap(response.data);
    return { id: String(v.id), productId: String(v.productId), versionTag: v.versionTag,
      changelog: v.changelog, releasedAt: v.releasedAt, createdAt: v.createdAt, updatedAt: v.updatedAt };
  },

  async update(id: string | number, data: any): Promise<ProductVersionModel> {
    const response = await apiClient.put(ENDPOINTS.PRODUCT_VERSIONS.UPDATE(id), data);
    const v = unwrap(response.data);
    return { id: String(v.id), productId: String(v.productId), versionTag: v.versionTag,
      changelog: v.changelog, releasedAt: v.releasedAt, createdAt: v.createdAt, updatedAt: v.updatedAt };
  },

  async delete(id: string | number): Promise<void> {
    await apiClient.delete(ENDPOINTS.PRODUCT_VERSIONS.DELETE(id));
  },
};

export const RoadmapItemsService = {
  async listByProduct(productId: string | number): Promise<ProductRoadmapItemModel[]> {
    const response = await apiClient.get(ENDPOINTS.ROADMAP_ITEMS.BY_PRODUCT(productId));
    const unwrapped = unwrap(response.data);
    return Array.isArray(unwrapped) ? unwrapped.map((r: any) => ({
      id: String(r.id), productId: String(r.productId), title: r.title,
      status: r.status, priority: r.priority, plannedRelease: r.plannedRelease,
      meta: r.meta, createdAt: r.createdAt, updatedAt: r.updatedAt,
    })) : [];
  },

  async create(data: { productId: string; title: string; status?: string; priority?: number; plannedRelease?: string }): Promise<ProductRoadmapItemModel> {
    const response = await apiClient.post(ENDPOINTS.ROADMAP_ITEMS.CREATE, data);
    const r = unwrap(response.data);
    return { id: String(r.id), productId: String(r.productId), title: r.title,
      status: r.status, priority: r.priority, plannedRelease: r.plannedRelease,
      meta: r.meta, createdAt: r.createdAt, updatedAt: r.updatedAt };
  },

  async update(id: string | number, data: any): Promise<ProductRoadmapItemModel> {
    const response = await apiClient.put(ENDPOINTS.ROADMAP_ITEMS.UPDATE(id), data);
    const r = unwrap(response.data);
    return { id: String(r.id), productId: String(r.productId), title: r.title,
      status: r.status, priority: r.priority, plannedRelease: r.plannedRelease,
      meta: r.meta, createdAt: r.createdAt, updatedAt: r.updatedAt };
  },

  async delete(id: string | number): Promise<void> {
    await apiClient.delete(ENDPOINTS.ROADMAP_ITEMS.DELETE(id));
  },
};

export const ProductDocumentsService = {
  async list(productId: string | number): Promise<ProductDocumentModel[]> {
    const response = await apiClient.get(ENDPOINTS.PRODUCTS.DOCUMENTS(productId));
    const unwrapped = unwrap(response.data);
    return Array.isArray(unwrapped) ? unwrapped.map((d: any) => ({
      id: String(d.id), filename: d.filename, mime: d.mime, size: d.size,
      url: d.url, createdAt: d.createdAt,
    })) : [];
  },

  async upload(productId: string | number, file: File, data?: { title?: string; folder?: string; description?: string }): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    if (data?.title) formData.append('title', data.title);
    if (data?.folder) formData.append('folder', data.folder);
    if (data?.description) formData.append('description', data.description);
    const response = await apiClient.post(ENDPOINTS.PRODUCTS.DOCUMENTS_UPLOAD(productId), formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const doc = unwrap(response.data);
    return {
      id: String(doc.id),
      filename: doc.filename,
      mime: doc.mime,
      size: doc.size,
      url: doc.url,
      createdAt: doc.createdAt,
    };
  },

  async delete(productId: string | number, documentId: string | number): Promise<void> {
    await apiClient.delete(ENDPOINTS.PRODUCTS.DOCUMENT_DELETE(productId, documentId));
  },
};

export const ProductGalleryService = {
  async list(productId: string | number): Promise<ProductGalleryModel[]> {
    const response = await apiClient.get(ENDPOINTS.PRODUCTS.GALLERY(productId));
    const unwrapped = unwrap(response.data);
    return Array.isArray(unwrapped) ? unwrapped.map((img: any) => ({
      id: String(img.id),
      url: img.url || '',
      filename: img.filename,
      alt: img.alt || img.filename,
      isPrimary: img.isPrimary === true,
      order: img.order || 0,
      createdAt: img.createdAt,
    })) : [];
  },

  async upload(productId: string | number, file: File, data?: { alt?: string; isPrimary?: boolean }): Promise<ProductGalleryModel> {
    const formData = new FormData();
    formData.append('file', file);
    if (data?.alt) formData.append('alt', data.alt);
    if (data?.isPrimary !== undefined) formData.append('isPrimary', String(data.isPrimary));
    const response = await apiClient.post(ENDPOINTS.PRODUCTS.GALLERY_UPLOAD(productId), formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const img = unwrap(response.data);
    return {
      id: String(img.id),
      url: img.url || '',
      filename: img.filename,
      alt: img.alt || img.filename,
      isPrimary: img.isPrimary === true,
      order: img.order || 0,
      createdAt: img.createdAt,
    };
  },

  async delete(productId: string | number, imageId: string | number): Promise<void> {
    await apiClient.delete(ENDPOINTS.PRODUCTS.GALLERY_DELETE(productId, imageId));
  },
};

export const ProductAuditLogsService = {
  async list(productId: string | number): Promise<ProductHistoryModel[]> {
    const response = await apiClient.get(ENDPOINTS.AUDIT_LOGS.LIST, {
      params: { tableName: 'Product', recordId: String(productId) },
    });
    const unwrapped = unwrap(response.data);
    return Array.isArray(unwrapped) ? unwrapped.map((log: any) => ({
      id: String(log.id),
      action: `${log.operation} em ${log.tableName}`,
      user: log.actorId || 'Sistema',
      time: log.changedAt,
      beforeData: log.beforeData,
      afterData: log.afterData,
    })) : [];
  },
};
